/**
 * Store Locator with OpenStreetMap (Leaflet.js)
 * Handles map initialization, store markers, search, and filtering
 */

if (!window.StoreLocator) {
  window.StoreLocator = class {
    constructor(sectionId, options = {}) {
      this.sectionId = sectionId;
      this.options = {
        defaultZoom: options.defaultZoom || 10,
        defaultLat: parseFloat(options.defaultLat) || 0,
        defaultLng: parseFloat(options.defaultLng) || 0,
        enableCityFilter: options.enableCityFilter !== false,
        markerIconUrl: options.markerIconUrl || null,
        markerColor: options.markerColor || '#007bff'
      };

      this.map = null;
      this.markers = [];
      this.stores = [];
      this.filteredStores = [];
      this.currentMarker = null;
      this.markerGroup = null;

      this.init();
    }

    init() {
      // Load Leaflet CSS and JS if not already loaded
      this.loadLeaflet().then(() => {
        // Wait a bit to ensure DOM is ready
        setTimeout(() => {
          this.collectStores();
          this.initMap();
          this.initSearch();
          this.initCityFilter();
          
          // Wait for map to be ready before updating
          if (this.map) {
            this.map.whenReady(() => {
              setTimeout(() => {
                this.updateMap();
              }, 200);
            });
          } else {
            // Retry if map is not ready
            setTimeout(() => {
              if (this.map) {
                this.updateMap();
              }
            }, 500);
          }
        }, 100);
      });
    }

    loadLeaflet() {
      return new Promise((resolve) => {
        // Check if Leaflet is already loaded
        if (window.L && window.L.map) {
          resolve();
          return;
        }

        // Load Leaflet CSS
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
          link.crossOrigin = '';
          document.head.appendChild(link);
        }

        // Load Leaflet JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        script.onload = resolve;
        document.body.appendChild(script);
      });
    }

    collectStores() {
      const storeItems = document.querySelectorAll(`#store-list-${this.sectionId} .store-locator-item`);
      
      this.stores = Array.from(storeItems).map((item) => {
        const lat = parseFloat(item.dataset.storeLat);
        const lng = parseFloat(item.dataset.storeLng);
        
        return {
          id: item.dataset.storeId,
          element: item,
          name: item.dataset.storeName || '',
          city: item.dataset.storeCity || '',
          address: item.dataset.storeAddress || '',
          phone: item.dataset.storePhone || '',
          hours: item.dataset.storeHours || '',
          mapUrl: item.dataset.storeMapUrl || '',
          lat: (isNaN(lat) || lat === 0) ? null : lat,
          lng: (isNaN(lng) || lng === 0) ? null : lng
        };
      }).filter(store => store.lat !== null && store.lng !== null && !isNaN(store.lat) && !isNaN(store.lng));

      this.filteredStores = [...this.stores];
    }

    initMap() {
      const mapElement = document.getElementById(`store-map-${this.sectionId}`);
      if (!mapElement) {
        // Retry if element is not ready
        setTimeout(() => this.initMap(), 100);
        return;
      }

      // Determine initial center and zoom
      let centerLat = this.options.defaultLat;
      let centerLng = this.options.defaultLng;
      let zoom = this.options.defaultZoom;

      if (this.stores.length > 0) {
        // Calculate center from all stores
        const validStores = this.stores.filter(s => s.lat !== null && s.lng !== null && !isNaN(s.lat) && !isNaN(s.lng));
        if (validStores.length > 0) {
          const avgLat = validStores.reduce((sum, s) => sum + s.lat, 0) / validStores.length;
          const avgLng = validStores.reduce((sum, s) => sum + s.lng, 0) / validStores.length;
          centerLat = avgLat;
          centerLng = avgLng;
        }
      }

      // Initialize map (scroll zoom only after click, so page scroll is not captured)
      this.map = L.map(mapElement, {
        zoomControl: true,
        scrollWheelZoom: false
      }).setView([centerLat, centerLng], zoom);

      this.map.on('click', () => {
        this.map.scrollWheelZoom.enable();
      });

      mapElement.addEventListener('mouseleave', () => {
        this.map.scrollWheelZoom.disable();
      });

      // Add OpenStreetMap tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(this.map);

      // Create marker group for clustering
      this.markerGroup = L.layerGroup().addTo(this.map);

      // Wait for map to be fully loaded before adding markers
      this.map.whenReady(() => {
        // Handle map resize
        setTimeout(() => {
          this.map.invalidateSize();
        }, 100);
      });
    }

    initSearch() {
      const searchInput = document.getElementById(`store-search-${this.sectionId}`);
      if (!searchInput) return;

      let searchTimeout;
      searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.filterStores(e.target.value);
        }, 300);
      });
    }

    initCityFilter() {
      if (!this.options.enableCityFilter) return;

      const cityFilter = document.getElementById(`city-filter-${this.sectionId}`);
      if (!cityFilter) return;

      // Populate city filter
      const cities = [...new Set(this.stores.map(s => s.city).filter(Boolean))].sort();
      cities.forEach(city => {
        const option = document.createElement('option');
        option.value = city;
        option.textContent = city;
        cityFilter.appendChild(option);
      });

      cityFilter.addEventListener('change', (e) => {
        this.filterStores(null, e.target.value);
      });
    }

    filterStores(searchTerm = null, cityFilter = null) {
      const searchInput = document.getElementById(`store-search-${this.sectionId}`);
      const citySelect = document.getElementById(`city-filter-${this.sectionId}`);
      
      const search = searchTerm !== null ? searchTerm : (searchInput ? searchInput.value : '');
      const city = cityFilter !== null ? cityFilter : (citySelect ? citySelect.value : '');

      const searchLower = search.toLowerCase().trim();
      const cityLower = city.toLowerCase().trim();

      this.filteredStores = this.stores.filter(store => {
        const matchesSearch = !searchLower || 
          store.name.toLowerCase().includes(searchLower) ||
          store.address.toLowerCase().includes(searchLower) ||
          store.city.toLowerCase().includes(searchLower);
        
        const matchesCity = !cityLower || store.city.toLowerCase() === cityLower;

        return matchesSearch && matchesCity;
      });

      this.updateStoreList();
      this.updateMap();
    }

    updateStoreList() {
      const storeList = document.getElementById(`store-list-${this.sectionId}`);
      const emptyState = document.getElementById(`store-empty-${this.sectionId}`);
      
      if (!storeList) return;

      // Show/hide stores
      this.stores.forEach(store => {
        const isVisible = this.filteredStores.includes(store);
        store.element.style.display = isVisible ? '' : 'none';
      });

      // Show/hide empty state
      if (emptyState) {
        emptyState.style.display = this.filteredStores.length === 0 ? 'block' : 'none';
      }

      // Update city filter if needed
      if (this.options.enableCityFilter) {
        const cityFilter = document.getElementById(`city-filter-${this.sectionId}`);
        if (cityFilter && !cityFilter.value) {
          // If no city filter is selected, show all cities in dropdown
          const visibleCities = [...new Set(this.filteredStores.map(s => s.city).filter(Boolean))].sort();
          // Keep the "All Cities" option and update visible cities
        }
      }
    }

    updateMap() {
      if (!this.map || !this.markerGroup) {
        // Retry if map is not ready
        setTimeout(() => this.updateMap(), 100);
        return;
      }

      // Clear existing markers
      this.markerGroup.clearLayers();
      this.markers = [];

      if (this.filteredStores.length === 0) {
        // Reset to default view if no stores
        if (this.stores.length > 0) {
          const avgLat = this.stores.reduce((sum, s) => sum + s.lat, 0) / this.stores.length;
          const avgLng = this.stores.reduce((sum, s) => sum + s.lng, 0) / this.stores.length;
          this.map.setView([avgLat, avgLng], this.options.defaultZoom);
        }
        return;
      }

      // Create custom icon if provided, otherwise create colored SVG marker
      let markerIcon = null;
      if (this.options.markerIconUrl) {
        markerIcon = L.icon({
          iconUrl: this.options.markerIconUrl,
          iconSize: [32, 32],
          iconAnchor: [16, 32],
          popupAnchor: [0, -32]
        });
      } else {
        // Create colored SVG marker
        markerIcon = this.createColoredMarker(this.options.markerColor);
      }

      // Add markers for filtered stores
      const bounds = [];
      this.filteredStores.forEach(store => {
        if (store.lat === null || store.lng === null || isNaN(store.lat) || isNaN(store.lng)) {
          return;
        }

        // Create marker with icon
        const markerOptions = {};
        if (markerIcon) {
          markerOptions.icon = markerIcon;
        }

        const marker = L.marker([store.lat, store.lng], markerOptions);

        // Create popup content
        let popupContent = `<div class="store-locator-popup">`;
        popupContent += `<h3 class="store-locator-popup-name">${this.escapeHtml(store.name)}</h3>`;
        if (store.city) {
          popupContent += `<p class="store-locator-popup-city">${this.escapeHtml(store.city)}</p>`;
        }
        if (store.address) {
          popupContent += `<p class="store-locator-popup-address">${this.escapeHtml(store.address)}</p>`;
        }
        if (store.phone) {
          // Clean phone number for WhatsApp (remove spaces, dashes, parentheses, plus, dots)
          const phoneClean = store.phone.replace(/[\s\-\(\)\+\.]/g, '');
          popupContent += `<p class="store-locator-popup-phone"><a href="https://wa.me/${phoneClean}" target="_blank" rel="noopener noreferrer" class="store-locator-popup-whatsapp-link">`;
          popupContent += `<svg class="store-locator-popup-whatsapp-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">`;
          popupContent += `<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" fill="currentColor"/>`;
          popupContent += `</svg> <span>${this.escapeHtml(store.phone)}</span></a></p>`;
        }
        if (store.hours) {
          popupContent += `<p class="store-locator-popup-hours">${this.escapeHtml(store.hours)}</p>`;
        }

        if (store.mapUrl) {
          popupContent += `<div style="margin-top: 10px;">`;
          popupContent += `<a href="${store.mapUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 6px 12px; background-color: var(--store-locator-button-bg, #007bff); color: var(--store-locator-button-text, #ffffff); text-decoration: none; border-radius: 4px; font-size: 12px;">View on Google Maps</a>`;
          popupContent += `</div>`;
        }

        popupContent += `</div>`;

        marker.bindPopup(popupContent, {
          className: 'store-locator-popup-wrapper'
        });
        
        // Add marker to group
        marker.addTo(this.markerGroup);

        // Store reference
        marker.store = store;
        this.markers.push(marker);
        bounds.push([store.lat, store.lng]);

        // Add click handler to store item
        const viewButton = store.element.querySelector('[data-action="view-on-map"]');
        if (viewButton) {
          viewButton.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.focusStore(store);
          };
        }

        // Add click handler to store item itself
        store.element.onclick = () => {
          this.focusStore(store);
        };
      });

      // Fit map to show all markers
      if (bounds.length > 0) {
        // Wait a bit for markers to render
        setTimeout(() => {
          if (bounds.length === 1) {
            this.map.setView(bounds[0], 15);
          } else {
            this.map.fitBounds(bounds, { padding: [50, 50] });
          }
          // Ensure markers are visible
          this.map.invalidateSize();
        }, 150);
      } else {
        // Invalidate size to ensure proper rendering
        setTimeout(() => {
          this.map.invalidateSize();
        }, 100);
      }
    }

    focusStore(store) {
      if (!this.map || !store) return;

      // Find marker for this store
      const marker = this.markers.find(m => m.store.id === store.id);
      if (marker) {
        this.map.setView([store.lat, store.lng], 15);
        marker.openPopup();
        
        // Highlight store item
        this.stores.forEach(s => {
          s.element.classList.remove('store-locator-item--active');
        });
        store.element.classList.add('store-locator-item--active');
        
        // Scroll store item into view
        store.element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    createColoredMarker(color) {
      // Create SVG marker with custom color
      const svgIcon = `
        <svg width="32" height="41" viewBox="0 0 32 41" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.163 0 0 7.163 0 16c0 11.045 16 25 16 25s16-13.955 16-25C32 7.163 24.837 0 16 0z" fill="${color}"/>
          <circle cx="16" cy="16" r="6" fill="#ffffff"/>
        </svg>
      `;
      
      return L.divIcon({
        html: svgIcon,
        className: 'store-locator-custom-marker',
        iconSize: [32, 41],
        iconAnchor: [16, 41],
        popupAnchor: [0, -41]
      });
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  };
}
